import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { onlineManager } from '@tanstack/react-query';

export type ConnectionBannerStatus = 'offline' | 'reconnected' | null;

const RECONNECTED_BANNER_DURATION_MS = 2000;

function useIsOnline(): boolean {
  return useSyncExternalStore(
    cb => onlineManager.subscribe(cb),
    () => onlineManager.isOnline(),
    () => true,
  );
}

export function useConnectionStatus(): ConnectionBannerStatus {
  const isOnline = useIsOnline();
  const [status, setStatus] = useState<ConnectionBannerStatus>(() =>
    isOnline ? null : 'offline',
  );
  const wasOfflineRef = useRef(!isOnline);

  useEffect(() => {
    if (!isOnline) {
      wasOfflineRef.current = true;
      setStatus('offline');
      return;
    }
    if (wasOfflineRef.current) {
      wasOfflineRef.current = false;
      setStatus('reconnected');
      const id = setTimeout(() => setStatus(null), RECONNECTED_BANNER_DURATION_MS);
      return () => clearTimeout(id);
    }
  }, [isOnline]);

  return status;
}
